import React, { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { BmSheet, Btn, Chip } from "../../../ForBrands/ui/BrandUI";
import { FormFieldM } from "../../../ForBrands/ui/mobile/MobileUI";
import ringSmComplete from "../../../../assets/brand/edit-hub/ring-sm-complete.svg";
import ringMdComplete from "../../../../assets/brand/edit-hub/ring-md-complete.svg";
import check16 from "../../../../assets/brand/edit-hub/check-16.svg";
import check20 from "../../../../assets/brand/edit-hub/check-20.svg";
import chevron from "../../../../assets/brand/edit-hub/chevron-14x8.svg";
import insightGlyph from "../../../../assets/brand/edit-hub/insight-24.svg";
import arrow from "../../../../assets/brand/edit-hub/arrow-18.svg";
import Icon from "../../../ForBrands/ui/icons";

// Pieces of the Edit profile hub, sized from the Figma components:
// ProgressRing, ProfileSectionRow, FormField, TagInput, Tag, SettingRow,
// Toggle and the section cards.

// How the section pieces lay out. The athlete's own editor sets
// { phone: true } around a section screen on phones (EditHubPhone.js), and
// the pieces take the Figma mobile look (athlete-edit 01 to 09): flat blocks
// instead of cards, 56px fields, two-up pairs. Everywhere else, the desktop
// editor, the Circle member's editor and the coach profile hub that borrows
// these pieces, there is no provider and nothing changes.
export const EditLayoutContext = createContext(Object.freeze({ phone: false }));
export const useEditLayout = () => useContext(EditLayoutContext);

// Progress ring: the arc starts at 12 o'clock; complete swaps in the check.
// `labelClass` sets the value's type (the phone section head's 44px ring
// reads it in bm-label-l, D-e9); by default 64px rings use bm-num-m and
// smaller ones bm-label-xs.
export const Ring = ({ size = 32, pct = 0, complete = false, label, labelClass }) => {
  if (complete) {
    const md = size >= 44;
    return (
      <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
        <img src={md ? ringMdComplete : ringSmComplete} alt="" width={size} height={size} className="block" />
        <img src={md ? check20 : check16} alt="" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: md ? 20 : 16, height: md ? 20 : 16 }} />
      </span>
    );
  }
  const sw = size >= 64 ? 5 : size >= 44 ? 4 : 3;
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct));
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} className="stroke-bm-mutedbg" strokeWidth={sw} fill="none" />
        {p > 0 && <circle cx={size / 2} cy={size / 2} r={r} className="stroke-bm-orange" strokeWidth={sw} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - p / 100)} />}
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-bm-ink ${labelClass || (size >= 64 ? "bm-num-m" : "bm-label-xs")}`}>{label ?? `${p}%`}</span>
    </span>
  );
};

// A 20px stroke icon recoloured through a mask so the active row reads ink.
export const NavIcon = ({ src, active }) => (
  <span
    aria-hidden="true"
    className={`inline-block h-5 w-5 shrink-0 ${active ? "bg-bm-ink" : "bg-bm-muted"}`}
    style={{ WebkitMaskImage: `url(${src})`, maskImage: `url(${src})`, WebkitMaskSize: "20px 20px", maskSize: "20px 20px", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center" }}
  />
);

// On phones a card has no chrome (Figma "block"): a titled one stacks its
// head and content 14px apart, an untitled one is just its fields, 24px apart.
// The title is an h3 on desktop, under the section head's h2. A phone section
// screen has only its hidden h1 (EditHubPhone.js), so there it is an h2.
export const HubCard = ({ title, desc, children, className = "" }) => {
  const { phone } = useEditLayout();
  const frame = phone
    ? `flex w-full flex-col ${title ? "gap-[14px]" : "gap-6"} ${className}`
    : `flex w-full flex-col gap-5 rounded-[12px] border border-bm-mutedbg bg-white p-6 ${className}`;
  const Title = phone ? "h2" : "h3";
  return (
    <div className={frame}>
      {title && (
        <div className="flex flex-col gap-1">
          <Title className="bm-h-m text-bm-ink">{title}</Title>
          {desc && <p className="bm-body-m text-bm-muted">{desc}</p>}
        </div>
      )}
      {children}
    </div>
  );
};

// `pair` marks one half of a two-up row in a phone Grid2 (Name | Surname,
// Hometown city | State); every other field there takes the full width.
export const FormField = ({ label, optional = false, hint, error, pair = false, children, className = "" }) => {
  const { phone } = useEditLayout();
  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className}`} data-pair={phone && pair ? "" : undefined}>
      {label && (
        <div className="flex items-center gap-2">
          <span className="bm-label-l text-bm-ink">{label}</span>
          {optional && <span className="bm-body-s text-bm-tertiary">Optional</span>}
        </div>
      )}
      {children}
      {error ? <span className="bm-body-s text-bm-critical">{error}</span> : hint ? <span className="bm-body-s text-bm-tertiary">{hint}</span> : null}
    </div>
  );
};

// Two columns from 640px. On phones only the fields marked `pair` sit two-up
// (12px apart); the rest take the full width, 24px apart.
export const Grid2 = ({ children }) => {
  const { phone } = useEditLayout();
  if (phone) return <div className="grid grid-cols-2 gap-x-3 gap-y-6 [&>*]:col-span-2 [&>[data-pair]]:col-span-1">{children}</div>;
  return <div className="grid gap-6 sm:grid-cols-2">{children}</div>;
};

export const HubInput = ({ className = "", ...props }) => <input className={`bm-input ${className}`} {...props} />;
// Phones take the 140px Textarea LG from the .bm-phone-form scope.
export const HubTextArea = ({ className = "", ...props }) => {
  const { phone } = useEditLayout();
  return <textarea className={`bm-input bm-textarea ${phone ? "" : "!h-[120px] !py-[10px] "}${className}`} {...props} />;
};
// On phones the .bm-phone-form scope draws the same 14x8 chevron 18px from
// the edge as the select's background, so the overlay image is left out.
export const HubSelect = ({ className = "", children, ...props }) => {
  const { phone } = useEditLayout();
  if (phone) {
    return (
      <span className={`relative block ${className}`}>
        <select className="bm-input bm-select" {...props}>{children}</select>
      </span>
    );
  }
  return (
    <span className={`relative block ${className}`}>
      <select className="bm-input bm-select !pr-10" {...props}>{children}</select>
      <img src={chevron} alt="" className="pointer-events-none absolute right-[14px] top-1/2 h-2 w-[14px] -translate-y-1/2" />
    </span>
  );
};

export const PrimaryBtn = ({ children, className = "", ...props }) => (
  <button type="button" className={`flex h-10 shrink-0 items-center justify-center rounded-full bg-bm-orange px-[18px] bm-label-l text-white hover:bg-[#e63d00] disabled:cursor-not-allowed disabled:bg-bm-mutedbg disabled:text-bm-placeholder ${className}`} {...props}>{children}</button>
);
export const SecondaryBtn = ({ children, className = "", ...props }) => (
  <button type="button" className={`flex h-10 shrink-0 items-center justify-center rounded-full border border-bm-border bg-white px-[18px] bm-label-l text-bm-ink hover:bg-bm-subtle disabled:cursor-not-allowed disabled:text-bm-placeholder ${className}`} {...props}>{children}</button>
);
export const TextBtn = ({ children, className = "", ...props }) => (
  <button type="button" className={`flex h-10 shrink-0 items-center justify-center rounded-full px-[18px] bm-label-l text-bm-ink hover:underline ${className}`} {...props}>{children}</button>
);

export const OutlineBadge = ({ children }) => <span className="inline-flex h-[22px] shrink-0 items-center rounded-full border border-bm-strong px-[9px] bm-label-xs text-bm-ink">{children}</span>;

// Removable token (Figma "Tag"): label, optional "· Level", ✕.
// On phones a long label wraps instead of clipping (Work 08, 2253:10822): the
// tag grows to at most the full width, the label takes the lines it needs and
// the level and ✕ stay on the right. The ✕ keeps the Figma spacing (Tag
// 1972:824: 6px after the text, 8px to the edge) and gets a 24px tap target
// from a box drawn around it, which takes no room in the row. min-h-7 keeps
// a one-line tag at the Figma 28px.
export const Tag = ({ label, level, onRemove }) => {
  const { phone } = useEditLayout();
  const levelNode = level && <span className={`flex items-center gap-1${phone ? " shrink-0" : ""}`}><span className="text-bm-tertiary">·</span><span className="text-bm-muted">{level}</span></span>;
  if (phone) {
    return (
      <span className="inline-flex min-h-7 max-w-full items-center gap-[6px] rounded-full bg-bm-subtle py-[2px] pl-[10px] pr-2 bm-body-m text-bm-ink">
        <span className="min-w-0 flex-auto break-words">{label}</span>
        {levelNode}
        {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="relative shrink-0 bm-body-s text-bm-tertiary before:absolute before:left-1/2 before:top-1/2 before:h-6 before:w-6 before:-translate-x-1/2 before:-translate-y-1/2 hover:text-bm-critical">✕</button>}
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 items-center gap-[6px] rounded-full bg-bm-subtle pl-[10px] pr-2 bm-body-m text-bm-ink">
      <span>{label}</span>
      {levelNode}
      {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="bm-body-s text-bm-tertiary hover:text-bm-critical">✕</button>}
    </span>
  );
};

// A suggestion list under a field. On phones the page has a sticky header
// (56px) and the fixed AutosaveBar (about 117px) at the bottom, so a list
// that would run under the bar opens above its field when there is more room
// there, and is never taller than the room it has. The keyboard opens after
// the field takes focus and the page may then scroll the field into view, so
// the room is measured again while the list is open. Desktop keeps the list
// under the field (null placement).
const LIST_MAX = 240;
const PHONE_BAR_ROOM = 140; // AutosaveBar plus a small gap
const PHONE_HEAD_ROOM = 64; // stack header plus a small gap
const useListPlacement = (open, boxRef, phone) => {
  const [place, setPlace] = useState(null);
  useLayoutEffect(() => {
    if (!phone) return undefined;
    if (!open || !boxRef.current || typeof window === "undefined") { setPlace(null); return undefined; }
    const vv = window.visualViewport;
    const measure = () => {
      const box = boxRef.current;
      if (!box) return;
      const r = box.getBoundingClientRect();
      const bottomEdge = Math.min(window.innerHeight - PHONE_BAR_ROOM, vv ? vv.offsetTop + vv.height : Infinity);
      const topEdge = Math.max(PHONE_HEAD_ROOM, vv ? vv.offsetTop : 0);
      const below = bottomEdge - r.bottom - 4;
      const above = r.top - topEdge - 4;
      const up = below < LIST_MAX && above > below;
      const maxHeight = Math.max(120, Math.min(LIST_MAX, up ? above : below));
      setPlace((p) => (p && p.up === up && p.maxHeight === maxHeight ? p : { up, maxHeight }));
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    vv?.addEventListener?.("resize", measure);
    vv?.addEventListener?.("scroll", measure);
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      vv?.removeEventListener?.("resize", measure);
      vv?.removeEventListener?.("scroll", measure);
    };
  }, [open, phone, boxRef]);
  return place;
};
const LIST_BOX = "absolute left-0 z-20 max-h-[240px] w-full overflow-y-auto rounded-[12px] border border-bm-border bg-white p-1 shadow-[0px_8px_24px_rgba(0,0,0,0.08)]";
const listProps = (place) => (place
  ? { className: `${LIST_BOX} ${place.up ? "bottom-full mb-1" : "top-full mt-1"}`, style: { maxHeight: place.maxHeight } }
  : { className: "absolute left-0 top-full z-20 mt-1 max-h-[240px] w-full overflow-y-auto rounded-[12px] border border-bm-border bg-white p-1 shadow-[0px_8px_24px_rgba(0,0,0,0.08)]" });

// Multi-value field: type, press Enter or comma, the value becomes a Tag.
// With `options` the typed text must match one of them (positions, sports).
export const TagInput = ({ values = [], onChange, options = null, suggestions = [], placeholder = "Add another…", allowCustom = true }) => {
  const { phone } = useEditLayout();
  // 16px typing on phones: iOS Safari zooms into a focused field set smaller (D-g6).
  const draftType = phone ? " !text-[16px] !leading-[22px]" : "";
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const labelOf = (v) => (options ? options.find((o) => o.value === v)?.label || String(v) : v);
  const pool = options ? options.filter((o) => !values.includes(o.value)) : suggestions.filter((s) => !values.includes(s)).map((s) => ({ value: s, label: s }));
  const matches = pool.filter((o) => o.label.toLowerCase().includes(draft.trim().toLowerCase())).slice(0, 8);
  const add = (v) => {
    if (v == null || v === "") return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft("");
    setOpen(false);
  };
  const commitDraft = () => {
    const text = draft.trim().replace(/,$/, "");
    if (!text) return;
    const exact = pool.find((o) => o.label.toLowerCase() === text.toLowerCase());
    if (exact) { add(exact.value); return; }
    if (options || !allowCustom) { if (matches[0]) add(matches[0].value); return; }
    add(text);
  };
  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const place = useListPlacement(open && matches.length > 0, boxRef, phone);
  return (
    <div ref={boxRef} className="relative">
      <div className="flex min-h-[44px] w-full flex-wrap items-center gap-2 rounded-[8px] border border-bm-border bg-white px-3 py-2 focus-within:border-bm-orange" onClick={() => boxRef.current?.querySelector("input")?.focus()}>
        {values.map((v) => <Tag key={String(v)} label={labelOf(v)} onRemove={() => onChange(values.filter((x) => x !== v))} />)}
        <input
          className={`bm-bare min-w-[120px] flex-1 bg-transparent bm-body-m text-bm-ink outline-none placeholder:text-bm-placeholder${draftType}`}
          value={draft}
          placeholder={values.length ? placeholder : (options ? "Pick one or more" : placeholder)}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commitDraft(); }
            if (e.key === "Backspace" && !draft && values.length) onChange(values.slice(0, -1));
            if (e.key === "Escape") setOpen(false);
          }}
          onBlur={() => { if (!options && allowCustom) commitDraft(); }}
        />
      </div>
      {open && matches.length > 0 && (
        <ul {...listProps(place)}>
          {matches.map((o) => (
            <li key={String(o.value)}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(o.value)} className="flex h-10 w-full items-center rounded-[8px] px-3 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">{o.label}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Select that also takes free text (schools and teams not in the list).
// `label` names the field for screen readers (the FormField label above it
// is plain text, not a <label>).
export const Combobox = ({ value, onChange, options = [], placeholder = "Select", label }) => {
  const { phone } = useEditLayout();
  const [draft, setDraft] = useState(value || "");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const q = draft.trim().toLowerCase();
  const matches = (q ? options.filter((o) => o.toLowerCase().includes(q)) : options).slice(0, 40);
  const pick = (v) => { onChange(v); setDraft(v); setOpen(false); };
  const place = useListPlacement(open && matches.length > 0, boxRef, phone);
  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <input
          className={phone ? "bm-input !pr-12" : "bm-input !pr-10"}
          aria-label={label}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => { if (draft.trim() !== (value || "")) onChange(draft.trim()); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pick(matches[0] && q && matches[0].toLowerCase() === q ? matches[0] : draft.trim()); } if (e.key === "Escape") setOpen(false); }}
        />
        <button type="button" tabIndex={-1} aria-label="Open list" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)} className={phone ? "absolute right-0 top-0 flex h-14 w-12 items-center justify-end pr-[18px]" : "absolute right-0 top-0 flex h-10 w-10 items-center justify-center"}>
          <img src={chevron} alt="" className="h-2 w-[14px]" />
        </button>
      </div>
      {open && matches.length > 0 && (
        <ul {...listProps(place)}>
          {matches.map((o) => (
            <li key={o}><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)} className="flex h-10 w-full items-center rounded-[8px] px-3 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">{o}</button></li>
          ))}
        </ul>
      )}
    </div>
  );
};

// The add sheet an ItemList opens on phones: its title and how to close it.
// The add forms read it and render inside a BmSheet; without it (desktop,
// the Circle editor, the coach hub) they render inline as before.
const AddSheetContext = createContext(null);
export const useAddSheet = () => useContext(AddSheetContext);

// The sheet around an add form (BottomSheet 2087:1790 / 2087:13686 /
// 2087:13759): the title from the list, the fields 16px apart in the phone
// form scope (56px controls), and one full-width 52px "Add" in the footer.
// The fields sit in a <form>, so the keyboard's Go / Enter adds as well.
export const AddSheet = ({ onAdd, disabled = false, children }) => {
  const sheet = useAddSheet();
  const formId = useId();
  const submit = (e) => { e.preventDefault(); if (!disabled) onAdd(); };
  return (
    <BmSheet
      title={sheet.title}
      onClose={sheet.onClose}
      footer={<Btn type="submit" form={formId} size="lg" className="w-full" disabled={disabled}>Add</Btn>}
    >
      <form id={formId} noValidate onSubmit={submit} className="bm-phone-form flex flex-col gap-4">{children}</form>
    </BmSheet>
  );
};

// Level picker in the add sheets (the "Level" frame 2087:1834): a row of
// Chips, the picked one dark. The stored values and web labels stay (D-e3),
// so honors and NIL deals keep Global.
export const LevelChips = ({ levels, value, onChange, label = "Level" }) => {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <span id={id} className="bm-label-l text-bm-ink">{label}</span>
      <div role="group" aria-labelledby={id} className="flex flex-wrap gap-2">
        {levels.map((l) => <Chip key={l} active={l === value} aria-pressed={l === value} onClick={() => onChange(l)}>{l}</Chip>)}
      </div>
    </div>
  );
};

// A card of removable items with an inline add form (Figma 07b).
// On phones (athlete-edit 06 to 08) it is a flat block: the tags one under
// the other, then a full-width 52px add button that opens the add form in a
// sheet titled `sheetTitle` (the button label when not given, D-e4).
export const ItemList = ({ title, desc, items = [], render, addLabel, sheetTitle, form, onAdd, onRemove, optional = false }) => {
  const { phone } = useEditLayout();
  const [adding, setAdding] = useState(false);
  const tags = items.map((it, i) => { const r = render(it); return <Tag key={`${r.label}-${i}`} label={r.label} level={r.level} onRemove={() => onRemove(i)} />; });
  if (phone) {
    return (
      <HubCard title={title} desc={desc}>
        {items.length > 0 && <div className="flex flex-col items-start gap-2">{tags}</div>}
        <Btn variant="secondary" size="lg" className="w-full" onClick={() => setAdding(true)}>{addLabel}</Btn>
        {adding && (
          <AddSheetContext.Provider value={{ title: sheetTitle || addLabel, onClose: () => setAdding(false) }}>
            {form({ submit: (item) => { onAdd(item); setAdding(false); } })}
          </AddSheetContext.Provider>
        )}
      </HubCard>
    );
  }
  return (
    <HubCard title={title} desc={desc}>
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {tags}
        </div>
      )}
      {adding ? (
        <div className="flex flex-col gap-3">
          {form({ submit: (item) => { onAdd(item); setAdding(false); } })}
          <div><TextBtn onClick={() => setAdding(false)}>Cancel</TextBtn></div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <SecondaryBtn onClick={() => setAdding(true)}>{addLabel}</SecondaryBtn>
          {optional && items.length === 0 && <span className="bm-body-s text-bm-tertiary">Optional</span>}
        </div>
      )}
    </HubCard>
  );
};

// Name + level inline add row (honors, achievements, activities). In an add
// sheet (06a, 07a, 07b): "Item name", the level chips, then Add.
export const NameLevelForm = ({ levels, placeholder, submit }) => {
  const sheet = useAddSheet();
  const id = useId();
  const [name, setName] = useState("");
  const [level, setLevel] = useState(levels[0]);
  const go = () => { if (!name.trim()) return; submit({ name: name.trim(), level }); };
  if (sheet) {
    return (
      <AddSheet onAdd={go} disabled={!name.trim()}>
        <FormFieldM label="Item name" htmlFor={`${id}-name`}><HubInput id={`${id}-name`} autoFocus value={name} placeholder={placeholder} onChange={(e) => setName(e.target.value)} /></FormFieldM>
        <LevelChips levels={levels} value={level} onChange={setLevel} />
      </AddSheet>
    );
  }
  return (
    <div className="flex items-end gap-3">
      <FormField label="Item name" className="flex-1"><HubInput autoFocus value={name} placeholder={placeholder} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }} /></FormField>
      <FormField label="Level" className="w-[200px]"><HubSelect value={level} onChange={(e) => setLevel(e.target.value)}>{levels.map((l) => <option key={l}>{l}</option>)}</HubSelect></FormField>
      <PrimaryBtn onClick={go}>Add</PrimaryBtn>
    </div>
  );
};

export const HubToggle = ({ on, onChange, disabled, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-bm-orange" : "bg-bm-strong"} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}>
    <span className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.2)] transition-all ${on ? "left-[23px]" : "left-[3px]"}`} />
  </button>
);

export const SettingRow = ({ title, badge, desc, on, onChange, alwaysOn = false, note }) => (
  <div className="flex items-center gap-6 rounded-[12px] border border-bm-mutedbg bg-white px-6 py-5">
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2"><span className="bm-h-s text-bm-ink">{title}</span>{badge && <OutlineBadge>{badge}</OutlineBadge>}</div>
      <p className="bm-body-m text-bm-muted">{desc}</p>
      {note && <p className="bm-body-s text-bm-orange">{note}</p>}
    </div>
    {alwaysOn ? <OutlineBadge>Always on</OutlineBadge> : <HubToggle on={on} onChange={onChange} label={title} />}
  </div>
);

// On phones the head is the 16px Cache AI glyph and a "Cache AI" overline
// (InsightBox · privacy note 2086:13451).
export const Insight = ({ title = "Insight", children }) => {
  const { phone } = useEditLayout();
  if (phone) {
    return (
      <div className="flex w-full flex-col gap-3 rounded-[8px] border border-bm-border bg-white px-5 py-[18px]">
        <div className="flex items-center gap-[10px]"><img src={insightGlyph} alt="" className="h-[16.149px] w-4" /><span className="bm-overline">Cache AI</span></div>
        <p className="bm-body-m text-bm-muted">{children}</p>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-3 rounded-[8px] border border-bm-border bg-white px-6 py-[22px]">
      <div className="flex items-center gap-[10px]"><img src={insightGlyph} alt="" className="h-6 w-6" /><span className="bm-h-s text-bm-ink">{title}</span></div>
      <p className="bm-body-m text-bm-muted">{children}</p>
    </div>
  );
};

// The overall strength card (ProfileStrength 2082:1013): the ring, "N% complete",
// what is left and, until everything is done, "Up next" with Continue, which
// opens that section. The desktop sidebar and the phone hub both show it.
export const ProfileStrength = ({ hub, whose, onOpen }) => (
  <div className="flex flex-col gap-[14px] rounded-[16px] border border-bm-mutedbg bg-white px-[18px] py-4">
    <div className="flex items-center gap-[14px]">
      <Ring size={64} pct={hub.pct} complete={false} label={`${hub.pct}%`} />
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <span className="bm-h-m text-bm-ink">{whose} is {hub.pct}% complete</span>
        <span className="bm-body-m text-bm-muted">{hub.sectionsLeft ? `${hub.sectionsLeft} section${hub.sectionsLeft === 1 ? "" : "s"} to finish` : "Everything is filled in"}</span>
      </div>
    </div>
    {hub.upNext && (
      <>
        <div className="h-px w-full bg-bm-mutedbg" />
        <button type="button" onClick={() => onOpen(hub.upNext.section.id)} className="flex w-full items-center gap-3 text-left">
          <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span className="bm-overline !text-bm-tertiary">Up next</span>
            <span className="bm-h-s text-bm-ink">{hub.upNext.section.label}</span>
            <span className="bm-body-m text-bm-muted">{hub.upNext.item}</span>
          </span>
          <span className="flex items-center gap-1 bm-label-l text-bm-orange">Continue<img src={arrow} alt="" className="h-[18px] w-[18px]" /></span>
        </button>
      </>
    )}
  </div>
);

export const SaveStatus = ({ status, error }) => {
  if (status === "saving") return <span className="flex items-center gap-[6px] bm-body-m text-bm-muted"><span className="h-3 w-3 animate-spin rounded-full border-2 border-bm-border border-t-bm-orange" />Saving…</span>;
  if (status === "dirty") return <span className="bm-body-m text-bm-muted">Unsaved changes</span>;
  if (status === "error") return <span className="bm-body-m text-bm-critical">{error || "Could not save."}</span>;
  return <span className="flex items-center gap-[6px] bm-body-m text-bm-positive"><Icon name="check" size={16} strokeWidth={2} />All changes saved.</span>;
};

export const SectionHead = ({ section, progress, status, error }) => (
  <div className="flex items-center gap-4">
    {!section.setting && <Ring size={44} pct={progress.pct} complete={progress.complete} />}
    <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
      <h2 className="bm-h-l text-bm-ink">{section.label}</h2>
      {!section.setting && <span className="bm-label-l text-bm-ink">{progress.done} of {progress.total} done</span>}
      {section.desc && <p className="bm-body-m text-bm-muted">{section.desc}</p>}
    </div>
    <SaveStatus status={status} error={error} />
  </div>
);

export const SectionFooter = ({ onSave, saving, next, onNext }) => (
  <div className="flex items-start justify-between">
    <PrimaryBtn onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save"}</PrimaryBtn>
    {next && <SecondaryBtn onClick={onNext}>Next: {next.label}</SecondaryBtn>}
  </div>
);
